import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import GrowthHome from './components/pages/GrowthHome';
import SeoManager from './components/seo/SeoManager';
import CookieConsent from './components/ui/CookieConsent';

import { initializeAnalytics } from './utils/analytics';
import { initializeMarketingTools } from './utils/marketing';
import usePageTracking from './hooks/usePageTracking.ts';

const PrivacyPolicy = lazy(() => import('./components/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/pages/TermsOfService'));
const DataRequest = lazy(() => import('./components/pages/DataRequest'));
const SaaSProducts = lazy(() => import('./components/pages/SaaSProducts'));
const FacebookConnect = lazy(() => import('./components/pages/FacebookConnect'));
const SolutionPage = lazy(() => import('./components/pages/SolutionPage'));
const CaseStudies = lazy(() => import('./components/pages/CaseStudies'));
const SocialManagerProduct = lazy(() => import('./components/pages/SocialManagerProduct'));
const ProjectBriefing = lazy(() => import('./components/pages/ProjectBriefing'));
const CrmDashboard = lazy(() => import('./components/pages/CrmDashboard'));

const RouteFallback = () => (
  <div
    aria-live="polite"
    aria-label="Loading page"
    style={{
      minHeight: '58vh',
      display: 'grid',
      placeItems: 'center',
      background: '#fbfbfa',
      color: '#7a8087',
      fontSize: '12px',
      letterSpacing: '.08em',
    }}
  >
    MAIPROSolutions
  </div>
);

function App() {
  const location = useLocation();

  usePageTracking(location.pathname);

  useEffect(() => {
    initializeAnalytics();
    initializeMarketingTools();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cookieConsent' || e.key === 'cookiePreferences') {
        initializeAnalytics();
        initializeMarketingTools();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen">
      <SeoManager />
      <Header currentPath={location.pathname} />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<GrowthHome />} />
          <Route path="/automacao-ai" element={<SolutionPage solution="automation" />} />
          <Route path="/websites" element={<SolutionPage solution="websites" />} />
          <Route path="/apps-sistemas" element={<SolutionPage solution="apps" />} />
          <Route path="/growth-marketing" element={<SolutionPage solution="growth" />} />
          <Route path="/projetos" element={<CaseStudies />} />
          <Route path="/social-manager" element={<SocialManagerProduct />} />
          <Route path="/briefing" element={<ProjectBriefing />} />
          <Route path="/crm" element={<CrmDashboard />} />
          <Route path="/produtos" element={<SocialManagerProduct />} />
          <Route path="/products" element={<SaaSProducts />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/data-request" element={<DataRequest />} />
          <Route path="/facebook-connect" element={<FacebookConnect />} />
          <Route path="*" element={<GrowthHome />} />
        </Routes>
      </Suspense>
      <Footer />
      <CookieConsent />
    </div>
  );
}

export default App;
